import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Calendar } from "lucide-react";

const TARGET = new Date("2026-10-03T09:30:00+05:30").getTime();

function diff() {
    const now = Date.now();
    const ms = Math.max(0, TARGET - now);
    const days = Math.floor(ms / 86_400_000);
    const hours = Math.floor((ms % 86_400_000) / 3_600_000);
    const mins = Math.floor((ms % 3_600_000) / 60_000);
    const secs = Math.floor((ms % 60_000) / 1_000);
    return { days, hours, mins, secs, done: ms === 0 };
}

const Unit = ({ value, label }) => (
    <div className="flex min-w-0 items-baseline gap-1 whitespace-nowrap">
        <span className="font-display font-black text-lg sm:text-xl md:text-2xl text-white tabular-nums leading-none">
            {String(value).padStart(2, "0")}
        </span>
        <span className="text-[9px] uppercase tracking-[0.12em] text-zinc-500 font-mono sm:text-[10px] sm:tracking-[0.2em]">{label}</span>
    </div>
);

export default function StickyCountdown() {
    const [t, setT] = useState(diff());
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        setMounted(true);
        const id = setInterval(() => setT(diff()), 1000);
        return () => clearInterval(id);
    }, []);

    return (
        <AnimatePresence>
            {mounted && (
                <motion.div
                    initial={{ y: 100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: 100, opacity: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 1.5 }}
                    className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none"
                    data-testid="sticky-countdown"
                >
                    <div className="max-w-[1440px] mx-auto px-3 sm:px-4 md:px-12 pb-3 md:pb-5">
                        <div className="pointer-events-auto glass rounded-2xl md:rounded-full border border-white/10 px-3 sm:px-4 md:px-6 py-3 md:py-3.5 flex flex-col md:flex-row items-center md:items-center gap-3 md:gap-6 shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
                            {/* Status dot + label */}
                            <div className="flex items-center gap-3 md:flex-none">
                                <span className="relative flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FF6B35] opacity-75" />
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#FF6B35]" />
                                </span>
                                <div className="hidden md:flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-zinc-300 font-mono">
                                    <Calendar size={12} className="text-[#FF6B35]" />
                                    JAF · 03 Oct 2026
                                </div>
                                <div className="md:hidden text-[11px] uppercase tracking-[0.2em] text-zinc-300 font-mono">
                                    JAF 2026
                                </div>
                            </div>

                            {/* Divider */}
                            <div className="hidden md:block h-6 w-px bg-white/10" />

                            {/* Countdown */}
                            <div className="flex w-full items-center justify-between gap-1.5 sm:gap-3 md:w-auto md:justify-start md:gap-5">
                                <Unit value={t.days} label="d" />
                                <span className="text-zinc-700 font-display font-black text-base sm:text-lg">:</span>
                                <Unit value={t.hours} label="h" />
                                <span className="text-zinc-700 font-display font-black text-base sm:text-lg">:</span>
                                <Unit value={t.mins} label="m" />
                                <span className="text-zinc-700 font-display font-black text-base sm:text-lg">:</span>
                                <Unit value={t.secs} label="s" />
                            </div>

                            {/* CTA */}
                            <div className="md:flex-1 md:flex md:justify-end w-full md:w-auto">
                                <a
                                    href="#contact"
                                    data-testid="sticky-cta-register"
                                    className="group w-full md:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#FF6B35] hover:bg-[#FF8957] text-black font-bold uppercase tracking-wider text-xs px-5 py-2.5 transition-all hover:scale-[1.03]"
                                >
                                    Register Interest
                                    <ArrowUpRight size={14} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
                                </a>
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
